import { IAssigneeIdMap } from "@gtmhub/assignees";
import { IMetricMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { GoalService } from "@gtmhub/goals/goalService";
import { IGoal } from "@gtmhub/goals/models";
import { goalIsNeitherObfuscatedNorLocked, isOkrAbandoned } from "@gtmhub/goals/utils";
import { ICanLinkInfo, ICanLinkInfoRecord } from "@gtmhub/links";
import { localize } from "@gtmhub/localization";
import { IPlanningSession } from "@gtmhub/sessions";
import { IdMap } from "@gtmhub/util";
import { IComponentOptions, IController, IPromise, IQService } from "angular";
import { IMetric } from "../../models/models";
import { MetricLinkService } from "../../services/metricLinkService";
import { MetricService } from "../../services/metricService";
import { createContributorMetric } from "../../utils";
import { goalsFieldsInCascadingMetricForm, incrementNewlyCreatedMetricsByGoalMap, metricsFieldsInCascadingMetricForm } from "./cascading-metric-form.component";

export type ContributingGoal = IGoal & { metrics?: ICanLinkInfoRecord[] };
export type ContributingMetric = IMetric & ICanLinkInfoRecord;

export interface ICascadingMetricContributor {
  goal?: ContributingGoal;
  metric?: IMetric;
  contributionPercentage?: number;
  searchTerm?: string;
  isSearchFocused?: boolean;
}

export interface ICascadingMetricContributorComponentBindings {
  contributor: ICascadingMetricContributor;
  cascadingMetric: IMetric;
  assigneesIdMap: IAssigneeIdMap;
  firstLevelChildGoals: IGoal[];
  sessionIds: string[];
  sessionsMap: IdMap<IPlanningSession>;

  alreadySelectedMetricIds: string[];
  newlyCreatedMetricsByGoalMap: IdMap<number>;

  onChange(): void;
  onDeleteRow(): void;
  focusSearch(args?: { isSearchFocused: boolean }): void;
  closeSearchMenu(args?: { event: PointerEvent }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CascadingMetricContributorCtrl extends ICascadingMetricContributorComponentBindings {}
export class CascadingMetricContributorCtrl implements IController {
  obtainedGoals: IGoal[];
  isMetricsPerGoalLimitReached: boolean;
  methodologySettings: IMetricMethodologySettingsUI;

  static $inject = ["$q", "$element", "GoalService", "MetricService", "MetricLinkService", "OkrMethodologyFacade"];

  constructor(
    private $q: IQService,
    private $element: JQLite,
    private goalService: GoalService,
    private metricService: MetricService,
    private metricLinkService: MetricLinkService,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {}

  selectGoal(goal: IGoal): void {
    if (this.contributor.isSearchFocused) {
      this.contributor.isSearchFocused = false;
    }

    if (this.contributor.goal) {
      this.contributor.metric = null;
    }

    this.contributor.goal = <ContributingGoal>goal;
    this.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.sessionsMap[this.contributor.goal.sessionId]);

    const selectedGoalMetricsPromise: IPromise<IMetric[]> = this.contributor.goal.metrics
      ? this.$q.resolve(this.contributor.goal.metrics)
      : this.loadSelectedGoalMetrics(this.contributor.goal.id);

    selectedGoalMetricsPromise.then((metrics) => {
      const metricsIds = metrics.map((metric) => metric.id);

      if (!metricsIds.length) {
        this.setMetricsPerGoalLimitReached();
        return;
      }

      this.checkIfCanLinkMetrics(metricsIds).then(({ canLinkInfo }) => {
        this.contributor.goal.metrics = this.contributor.goal.metrics.map((metric) => ({ ...metric, ...canLinkInfo.cascading[metric.id] }));
        this.setMetricsPerGoalLimitReached();
      });
    });
  }

  selectMetric(metric: ContributingMetric): void {
    if (!metric.canLink) {
      return;
    }

    this.contributor.metric = angular.copy(metric);
    this.alreadySelectedMetricIds.push(metric.id);
  }

  createMetric(): void {
    this.contributor.metric = createContributorMetric(this.cascadingMetric, this.contributor.goal.id);
    incrementNewlyCreatedMetricsByGoalMap(this.newlyCreatedMetricsByGoalMap, this.contributor.goal.id);
  }

  deleteRow(): void {
    const metricIndex = this.alreadySelectedMetricIds.indexOf(this.contributor.metric?.id);
    if (metricIndex !== -1) {
      this.alreadySelectedMetricIds.splice(metricIndex, 1);
    }

    if (this.contributor.metric && !this.contributor.metric.id) {
      this.newlyCreatedMetricsByGoalMap[this.contributor.goal.id]--;
    }

    this.onDeleteRow();
  }

  onSearchTermChange(searchTerm: string): void {
    if (!searchTerm) return;

    this.goalService
      .getGoalsV2({
        filter: {
          $and: [{ name: { $regex: searchTerm } }, { sessionId: { $in: this.sessionIds } }],
        },
        fields: goalsFieldsInCascadingMetricForm,
      })
      .then(({ items }) => {
        this.obtainedGoals = (items as IGoal[]).filter((goal) => goalIsNeitherObfuscatedNorLocked(goal) && !isOkrAbandoned(goal.closedStatus));
      });
  }

  updateMetricOwners(ids: string[]): void {
    this.contributor.metric.ownerIds = ids;
  }

  dropdownToggled(isDropdownOpen: boolean): void {
    if (isDropdownOpen) {
      this.$element[0].querySelector<HTMLElement>("tags-input input").click();
    }
  }

  private loadSelectedGoalMetrics(goalId: string): IPromise<IMetric[]> {
    return this.metricService.getMetricsV2({ filter: { goalId: goalId }, fields: metricsFieldsInCascadingMetricForm }).then(({ items }) => items);
  }

  private checkIfCanLinkMetrics(metricsIds: string[]): IPromise<ICanLinkInfo> {
    return this.metricLinkService
      .checkIfCanLink({ sourceMetricId: this.cascadingMetric.id, linkType: "cascading", targetMetricIds: metricsIds })
      .then((canLinkInfo) => this.addCanLinkInfoToAlreadySelectedMetrics(canLinkInfo));
  }

  private addCanLinkInfoToAlreadySelectedMetrics(canLinkInfo: ICanLinkInfo): ICanLinkInfo {
    this.alreadySelectedMetricIds.forEach((selectedMetricId) => {
      canLinkInfo.canLinkInfo.cascading[selectedMetricId] = {
        canLink: false,
        reason: localize("a_metric_can_only_contribute_to_one_other_metric"),
      };
    });

    return canLinkInfo;
  }

  private setMetricsPerGoalLimitReached(): void {
    const goalSessionSettings = this.sessionsMap[this.contributor.goal.sessionId].settings;
    const limitMetricsPerGoal = this.okrMethodologyFacade.getLimitSetting("metric", goalSessionSettings);
    const currentMetricsCount = this.contributor.goal.metrics?.length || 0;
    // Dummy metrics will be created when the user clicks the CTA button
    const newlyCreatedMetrics = this.newlyCreatedMetricsByGoalMap[this.contributor.goal.id] || 0;

    this.isMetricsPerGoalLimitReached = currentMetricsCount + newlyCreatedMetrics >= limitMetricsPerGoal;
  }
}

export const CascadingMetricContributorComponent: IComponentOptions = {
  bindings: {
    cascadingMetric: "<",
    assigneesIdMap: "<",
    firstLevelChildGoals: "<",
    sessionIds: "<",
    sessionsMap: "<",

    contributor: "=",
    alreadySelectedMetricIds: "=",
    newlyCreatedMetricsByGoalMap: "=",

    onChange: "&",
    onDeleteRow: "&",
    focusSearch: "&",
    closeSearchMenu: "&",
  },
  controller: CascadingMetricContributorCtrl,
  templateUrl: require("./cascading-metric-contributor.jade"),
};
